import React, { FunctionComponent, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { AssetTypeModel } from '../types/AssetTypeModel';
import { assetTypeService } from '../api/asset-types/asset-type.service';
import { AssetTypeReadDTO } from '../api/asset-types/asset-type.contracts';
import { useTranslation } from '@/lib';

interface AssetTypesSelectProps {
  selectedAssetTypeIds: number[];
  onChange: (selectedAssetTypes: AssetTypeModel[]) => void;
}

const AssetTypesSelect: FunctionComponent<AssetTypesSelectProps> = ({ selectedAssetTypeIds, onChange }) => {
  const [assetTypes, setAssetTypes] = useState<AssetTypeModel[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchAssetTypes = async () => {
      const response = await assetTypeService.getNames();
      if (response.isSuccess) {
        // The names endpoint only returns id, name and code; wrap them in a model to keep the onChange contract.
        setAssetTypes(response.payload.map((name) => AssetTypeModel.fromDTO(name as AssetTypeReadDTO)));
      }
    };
    fetchAssetTypes();
  }, []);

  const handleSelectionChange = (event: React.SyntheticEvent, value: AssetTypeModel[]) => {
    onChange(value);
  };

  return (
    <Autocomplete
      multiple
      disableClearable
      options={assetTypes}
      value={assetTypes.filter((at) => selectedAssetTypeIds.includes(at.id))}
      onChange={handleSelectionChange}
      getOptionLabel={(option) => option.name}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={t('asset_types')}
          helperText={t('asset_types_helper_text')}
          slotProps={{
            ...params.slotProps,
            inputLabel: { shrink: true },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
    />
  );
};

export default AssetTypesSelect;
